/**
 * The undo on a session's "edited" card (RFC 0301 S9).
 *
 * The inline diff in the document carries the same undo. This one is for the
 * edit made while you were looking somewhere else: the card is where you find
 * it. Both undo the same change, and each reads history to say where it stands,
 * so whichever you click, the other shows it undone.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { undoChange } from '@mod/core/model'
import { useMemo, useState } from 'react'
import { readLocalPerson } from '../../services/identity/localPerson'
import { useDocument } from '../../services/world/repo/useDocument'
import { editStatus } from './cardUndo'

export function EditUndo({
  repo,
  projectId,
  documentId,
  hash,
}: {
  repo: Repo
  projectId: string
  documentId: string
  hash: string
}) {
  const [doc] = useDocument(documentId as AnyDocumentId)
  // Recomputed when the document changes, and only then: reading history is
  // not free on a long document.
  const status = useMemo(
    () => (doc ? editStatus(doc, hash) : 'unknown'),
    [doc, hash],
  )
  const [busy, setBusy] = useState(false)

  if (status === 'unknown') return null
  if (status === 'undone') {
    return (
      <span className="shrink-0 text-[0.72rem] text-muted-foreground">
        undone
      </span>
    )
  }
  if (status === 'changed') {
    return (
      <span
        className="shrink-0 text-[0.72rem] text-muted-foreground line-through"
        title="You changed this text since, so undo would revert your words"
      >
        undo
      </span>
    )
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true)
        // A refusal needs no message of its own: the document changed under
        // the click, and the status above redraws from history to say how.
        // RFC 0313: the undo carries who did it, and the session is told.
        const person = readLocalPerson()
        void undoChange(repo, projectId, documentId, hash, {
          ...(person
            ? { authorId: person.id, by: { id: person.id, name: person.name } }
            : {}),
        })
          .catch(() => {})
          .finally(() => setBusy(false))
      }}
      title="Undo this edit"
      className="shrink-0 rounded px-1 text-[0.72rem] text-blue-500 hover:bg-muted disabled:opacity-50"
    >
      undo
    </button>
  )
}
